import { useState, useCallback } from 'react';
import { fmtLocalDate } from '../../../utils/datetime';
import { sig, valueLabel, type ChartTheme, type Field, type Stat } from './controlChartData';

// ─────────────────────────────────────────────────────────────────────────────
// The chart canvases, ported from the prototype's SVG drawing routines.
//
// Three genuinely different charts, NOT three skins of one:
//   · <LjChart>      Levey-Jennings individuals — % recovery / RPD / log₁₀ count.
//                    Also draws the RANGE chart, which differs in kind: one upper
//                    criterion, no warning band, no lower control limit.
//   · <AttrChart>    Sterility. An attribute (grew / did not), so bars against a
//                    criterion of zero — there is no mean to plot.
//   · <OverlayChart> Every parameter at once, standardised to z-scores.
//
// Drawn as SVG rather than a chart library: the five limit lines, their right-
// margin labels and the per-point control colouring are the whole point of a
// control chart, and every library needs fighting to produce them. Geometry is
// the prototype's, so the two render identically.
//
// Colours come from the chart THEME (a closed token set per canvas), never from
// a hardcoded hex at a call site.
// ─────────────────────────────────────────────────────────────────────────────

export interface Pt { v: number; d: string }

interface TipData {
  title: string; colour: string; date: string;
  label: string; value: string; z?: string;
  status: string; kind: 'ok' | 'warn' | 'bad';
  x: number; y: number;
}

/** Shared hover card. The SVG <title> element was doing this job in an earlier
 *  cut, but it waits about a second, cannot be styled and shows one line of
 *  plain text. */
function Tip({ t }: { t: TipData | null }) {
  if (!t) return null;
  return (
    <div className="cc-tip" style={{ left: t.x, top: t.y }} role="tooltip">
      <div className="cc-tip-h"><i style={{ background: t.colour }} />{t.title}</div>
      <div className="cc-tip-r"><span>Date</span><b>{t.date}</b></div>
      <div className="cc-tip-r"><span>{t.label}</span><b>{t.value}</b></div>
      {t.z ? <div className="cc-tip-r"><span>z-score</span><b>{t.z}</b></div> : null}
      <div className={`cc-tip-s ${t.kind}`}>{t.status}</div>
    </div>
  );
}

/** Keeps the card inside the viewport — near the right or bottom edge it flips
 *  to the other side of the cursor instead of being clipped. */
function useTip() {
  const [tip, setTip] = useState<TipData | null>(null);
  const place = useCallback((e: React.MouseEvent, d: Omit<TipData, 'x' | 'y'>) => {
    const w = 210;
    const h = 118;
    let x = e.clientX + 16;
    let y = e.clientY + 16;
    if (x + w > window.innerWidth - 8) x = e.clientX - w - 16;
    if (y + h > window.innerHeight - 8) y = e.clientY - h - 16;
    setTip({ ...d, x: Math.max(8, x), y: Math.max(8, y) });
  }, []);
  return { tip, place, hide: () => setTip(null) };
}

// ── Levey-Jennings (and the range chart) ─────────────────────────────────────

export function LjChart({ pts, st, field, theme }:
{ pts: Pt[]; st: Stat; field: Field; theme: ChartTheme }) {
  const { tip, place, hide } = useTip();
  if (st.kind === 'attribute' || st.kind === 'presence') return <AttrChart pts={pts} st={st} theme={theme} />;
  if (!pts.length) return <div className="cc-none">No data to display</div>;

  const t = theme;
  const label = valueLabel(field);
  /* A range chart carries no warning band and no lower control limit, so its
     legend and its limit labels differ: the centre line is the mean range, the
     single upper line is the 3.27 R̄ criterion, and zero is a floor rather than
     a limit. */
  const rng = field === 'rng';

  // Right margin holds the limit-line labels. The longest is the range chart's
  // "Criterion 3.27R̄ 0.47" (~130px at 11px semibold); a narrower R clipped it at
  // the viewBox edge, so the range chart gets a wider right gutter.
  const W = 1000, H = 392, L = 62, R = rng ? 156 : 104, T = 40, B = 52;
  const iw = W - L - R, ih = H - T - B;

  const limits = [st.ucl, st.uwl, st.mean, st.lwl, st.lcl]
    .filter((v): v is number => v !== null && Number.isFinite(v));
  let lo = Math.min(...pts.map(p => p.v), ...limits);
  let hi = Math.max(...pts.map(p => p.v), ...limits);
  const pad = (hi - lo) * 0.14 || 1;
  lo -= pad; hi += pad;

  // "Nice" tick step, so the axis reads 90 / 95 / 100 rather than 91.4 / 97.8.
  const raw = (hi - lo) / 5;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const stepY = ([1, 2, 2.5, 5, 10].find(m => m * mag >= raw) || 10) * mag;
  lo = Math.floor(lo / stepY) * stepY;
  hi = Math.ceil(hi / stepY) * stepY;

  const y = (v: number) => T + ih - ((v - lo) / (hi - lo)) * ih;
  const x = (i: number) => pts.length === 1 ? L + iw / 2 : L + (iw * i) / (pts.length - 1);
  const ticks: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += stepY) ticks.push(+v.toFixed(6));

  const { red: RED, grn: GRN, ink: INK, ser: SER, amb: AMB } = t;
  const frame = t.dark ? 'rgba(255,255,255,.22)' : '#d5d9de';
  const every = Math.max(1, Math.ceil(pts.length / 7));

  const ln = (v: number | null, col: string, lab: string, dash?: string) =>
    (v === null || !Number.isFinite(v)) ? null : (
      <g key={lab}>
        <line x1={L} y1={y(v)} x2={L + iw} y2={y(v)} stroke={col} strokeWidth={1.2}
          strokeDasharray={dash} />
        <text x={L + iw + 8} y={y(v) + 3.5} fontSize={11} fontWeight={600} fill={col}>{lab}</text>
      </g>
    );

  const sw = (cx: number, col: string, lab: string, line?: boolean) => (
    <g key={lab}>
      {line
        ? <line x1={cx} y1={4} x2={cx + 15} y2={4} stroke={col} strokeWidth={2.4} />
        : <rect x={cx} y={0} width={9} height={9} rx={1.5} fill={col} />}
      <text x={cx + (line ? 21 : 14)} y={7.5} fontSize={10.5} fill={t.axis}>{lab}</text>
    </g>
  );

  return (
    <div className={`cc-plotwrap${t.dark ? ' cc-dark' : ''}`}>
      <div className="cc-chartbox">
        <svg viewBox={`0 0 ${W} ${H}`} className="cc-chart" role="img"
          aria-label={`Control chart for ${st.parameter}: mean ${sig(st.mean)}, control limits ${sig(st.lcl)} to ${sig(st.ucl)}`}>
          <rect width={W} height={H} fill={t.bg} />

          {/* Legend, centred above the plot — square swatches for the series,
              rules for the limit lines, the way the dashboard cards draw it. */}
          {(() => {
            // Items FLOW left-to-right with widths estimated from label length, so a
            // long series label (e.g. the micro "log₁₀ count (positive control)")
            // never overlaps the next entry (fixed X positions collided before).
            const items: { c: string; l: string; line?: boolean }[] = rng
              ? [{ c: SER, l: 'Duplicate range' }, { c: RED, l: 'Exceeds criterion' },
                 { c: INK, l: 'Mean range R̄', line: true }, { c: RED, l: 'Criterion 3.27 R̄', line: true }]
              : [{ c: SER, l: label }, { c: AMB, l: 'Warning' }, { c: RED, l: 'Out of control' },
                 { c: INK, l: 'Mean', line: true }, { c: GRN, l: '± 2SD', line: true }, { c: RED, l: '± 3SD', line: true }];
            let cx = 0;
            const els = items.map((it) => {
              const el = sw(cx, it.c, it.l, it.line);
              cx += (it.line ? 21 : 14) + it.l.length * 6 + 18;
              return el;
            });
            return <g transform={`translate(${(W / 2 - cx / 2).toFixed(0)}, 12)`}>{els}</g>;
          })()}

          <rect x={L} y={T} width={iw} height={ih} fill="none" stroke={frame} />
          {ticks.map(v => (
            <g key={v}>
              <line x1={L} y1={y(v)} x2={L + iw} y2={y(v)} stroke={t.grid} />
              <text x={L - 9} y={y(v) + 4} textAnchor="end" fontSize={11} fill={t.axis}>{+v.toFixed(2)}</text>
            </g>
          ))}
          <text transform={`translate(16, ${T + ih / 2}) rotate(-90)`} textAnchor="middle"
            fontSize={11} fill={t.axis}>{label}</text>

          {ln(st.ucl, RED, `${rng ? 'Criterion 3.27R̄ ' : 'UCL '}${sig(st.ucl)}`, '6 3')}
          {ln(st.uwl, GRN, `UWL ${sig(st.uwl)}`, '3 3')}
          {ln(st.mean, INK, `${rng ? 'R̄ ' : 'Mean '}${sig(st.mean)}`)}
          {ln(st.lwl, GRN, `LWL ${sig(st.lwl)}`, '3 3')}
          {ln(rng ? null : st.lcl, RED, `LCL ${sig(st.lcl)}`, '6 3')}

          <path d={pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ')}
            fill="none" stroke={SER} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />

          {pts.map((p, i) => {
            const out = (st.ucl !== null && p.v > st.ucl) || (st.lcl !== null && p.v < st.lcl);
            const warn = !out && ((st.uwl !== null && p.v > st.uwl) || (st.lwl !== null && p.v < st.lwl));
            const col = out ? RED : warn ? AMB : SER;
            const status = out ? 'Beyond a control limit' : warn ? 'Beyond a warning limit' : 'In control';
            return (
              <circle key={i} cx={x(i)} cy={y(p.v)} r={out ? 5 : warn ? 4.2 : 3.4}
                fill={col} stroke={t.bg} strokeWidth={1.3} className="cc-pt"
                onMouseEnter={e => place(e, {
                  title: st.parameter, colour: col, date: fmtLocalDate(p.d), label,
                  value: String(p.v), status, kind: out ? 'bad' : warn ? 'warn' : 'ok',
                })}
                onMouseMove={e => place(e, {
                  title: st.parameter, colour: col, date: fmtLocalDate(p.d), label,
                  value: String(p.v), status, kind: out ? 'bad' : warn ? 'warn' : 'ok',
                })}
                onMouseLeave={hide} />
            );
          })}

          {pts.map((p, i) => i % every ? null : (
            <text key={`d${i}`} x={x(i)} y={T + ih + 16} textAnchor="end"
              transform={`rotate(-35 ${x(i).toFixed(1)} ${T + ih + 16})`}
              fontSize={11} fill={t.axis}>{fmtLocalDate(p.d)}</text>
          ))}
        </svg>
      </div>
      <Tip t={tip} />
    </div>
  );
}

// ── Sterility (attribute) ────────────────────────────────────────────────────

export function AttrChart({ pts, st, theme }: { pts: Pt[]; st: Stat; theme: ChartTheme }) {
  const { tip, place, hide } = useTip();
  if (!pts.length) return <div className="cc-none">No data to display</div>;
  const t = theme;
  // Two attribute charts share this canvas. STERILITY (blank): the value is a
  // colony count and any growth (>0) is the excursion. PRESENCE/ABSENCE (positive
  // control): the value is 1 = present / 0 = absent, and the criterion is the
  // OPPOSITE — an ABSENT (the control failed to grow) is the excursion. Presence is
  // drawn as a failure indicator so both read "excursions rise above the line".
  const pres = st.kind === 'presence';
  const failOf = (v: number) => pres ? (v < 1 ? 1 : 0) : v;   // height above the baseline
  const W = 1000, H = 300, L = 62, R = 104, T = 40, B = 52;
  const iw = W - L - R, ih = H - T - B;
  const hi = pres ? 2 : Math.max(3, (st.maxGrowth ?? 0) + 1);
  const y = (v: number) => T + ih - (v / hi) * ih;
  const bw = Math.max(3, Math.min(16, (iw / pts.length) * 0.62));
  const x = (i: number) => pts.length === 1 ? L + iw / 2 : L + (iw * i) / (pts.length - 1);
  const ticks: number[] = [];
  for (let v = 0; v <= hi; v++) ticks.push(v);
  const { red: RED, grn: GRN } = t;
  const frame = t.dark ? 'rgba(255,255,255,.22)' : '#d5d9de';
  const every = Math.max(1, Math.ceil(pts.length / 7));

  const yLabel = pres ? 'Absent (control failed)' : 'Colonies in blank';
  const passLab = pres ? 'Present (pass)' : 'No growth (pass)';
  const failLab = pres ? 'Absent — control failed' : 'Growth — batch invalid';
  const critLab = pres ? 'Criterion: Present' : 'Criterion 0';
  const okStatus = pres ? 'Present' : 'No growth';
  const badStatus = pres ? 'Absent — positive control failed to grow' : 'Growth — sterility failed';
  const ptLabel = pres ? 'Presence' : 'Colonies in blank';
  const ptValue = (v: number) => pres ? (v < 1 ? 'Absent' : 'Present') : String(v);

  const sw = (cx: number, col: string, lab: string) => (
    <g key={lab}>
      <rect x={cx} y={0} width={9} height={9} rx={1.5} fill={col} />
      <text x={cx + 14} y={7.5} fontSize={10.5} fill={t.axis}>{lab}</text>
    </g>
  );

  return (
    <div className={`cc-plotwrap${t.dark ? ' cc-dark' : ''}`}>
      <div className="cc-chartbox">
        <svg viewBox={`0 0 ${W} ${H}`} className="cc-chart" role="img"
          aria-label={pres
            ? `Presence/Absence positive-control chart for ${st.parameter}: ${st.fail} of ${st.count} controls were absent`
            : `Sterility control chart for ${st.parameter}: ${st.fail} of ${st.count} blanks showed growth`}>
          <rect width={W} height={H} fill={t.bg} />
          <g transform={`translate(${(W / 2 - 140).toFixed(0)}, 12)`}>
            {sw(0, GRN, passLab)}{sw(150, RED, failLab)}
          </g>
          <rect x={L} y={T} width={iw} height={ih} fill="none" stroke={frame} />
          {ticks.map(v => (
            <g key={v}>
              <line x1={L} y1={y(v)} x2={L + iw} y2={y(v)} stroke={t.grid} />
              <text x={L - 9} y={y(v) + 4} textAnchor="end" fontSize={11} fill={t.axis}>{v}</text>
            </g>
          ))}
          <text transform={`translate(16, ${T + ih / 2}) rotate(-90)`} textAnchor="middle"
            fontSize={11} fill={t.axis}>{yLabel}</text>

          {/* The criterion sits on the baseline — zero growth for a blank, and for
              a positive control zero "absents" (it must always be present). */}
          <line x1={L} y1={y(0)} x2={L + iw} y2={y(0)} stroke={GRN} strokeWidth={1.6} />
          <text x={L + iw + 8} y={y(0) + 3.5} fontSize={11} fontWeight={600} fill={GRN}>{critLab}</text>

          {pts.map((p, i) => {
            const f = failOf(p.v);
            const bad = f > 0;
            const h = bad ? Math.max(3, y(0) - y(f)) : 2.5;
            return (
              <g key={i}>
                <rect x={x(i) - bw / 2} y={y(0) - h} width={bw} height={h}
                  fill={bad ? RED : GRN} className="cc-pt"
                  onMouseEnter={e => place(e, {
                    title: st.parameter, colour: bad ? RED : GRN, date: fmtLocalDate(p.d),
                    label: ptLabel, value: ptValue(p.v),
                    status: bad ? badStatus : okStatus, kind: bad ? 'bad' : 'ok',
                  })}
                  onMouseMove={e => place(e, {
                    title: st.parameter, colour: bad ? RED : GRN, date: fmtLocalDate(p.d),
                    label: ptLabel, value: ptValue(p.v),
                    status: bad ? badStatus : okStatus, kind: bad ? 'bad' : 'ok',
                  })}
                  onMouseLeave={hide} />
                {bad ? (
                  <text x={x(i)} y={y(f) - 6} textAnchor="middle" fontSize={10}
                    fontWeight="bold" fill={RED}>{pres ? 'Absent' : p.v}</text>
                ) : null}
              </g>
            );
          })}
          {pts.map((p, i) => i % every ? null : (
            <text key={`d${i}`} x={x(i)} y={T + ih + 16} textAnchor="end"
              transform={`rotate(-35 ${x(i).toFixed(1)} ${T + ih + 16})`}
              fontSize={11} fill={t.axis}>{fmtLocalDate(p.d)}</text>
          ))}
        </svg>
      </div>
      <Tip t={tip} />
    </div>
  );
}

// ── Overlay — every parameter, standardised ──────────────────────────────────

export interface OverlaySeries { name: string; col: string; dash?: string; pts: { z: number; d: string; v: number }[] }

/** Raw values cannot share an axis: Lead recovers around 99 with SD 5.5 while
 *  Fluoride swings on SD 44, so one would flatten the other and each would need
 *  its own five limit lines. Standardising to a z-score, (x − mean) / SD per
 *  parameter, puts them all on one scale where the limits become ±2SD and ±3SD
 *  for EVERY series at once — which is the whole point of looking at them
 *  together: you see which analyte is drifting, not which has bigger numbers. */
export function OverlayChart({ series, field, theme }:
{ series: OverlaySeries[]; field: Field; theme: ChartTheme }) {
  const { tip, place, hide } = useTip();
  const t = theme;
  const lbl = field === 'rpd' ? 'RPD' : '% Recovery';
  if (!series.length) {
    return (
      <div className="cc-none" style={{ padding: '64px 0' }}>
        <b>No series selected.</b><br />Tick a parameter below to plot it.
      </div>
    );
  }

  const W = 1000, H = 360, L = 52, R = 104, T = 22, B = 56;
  const iw = W - L - R, ih = H - T - B;
  const n = Math.max(...series.map(s => s.pts.length));
  const zs = series.flatMap(s => s.pts.map(p => p.z));
  const bound = Math.max(3.6, Math.ceil(Math.max(...zs.map(Math.abs)) * 2) / 2);

  const y = (z: number) => T + ih - ((z + bound) / (bound * 2)) * ih;
  const x = (i: number) => L + (iw * (i + 0.5)) / n;

  const ln = (z: number, col: string, lab: string, dash?: string) => (
    <g key={lab}>
      <line x1={L} y1={y(z)} x2={W - R} y2={y(z)} stroke={col} strokeWidth={1.4}
        strokeDasharray={dash} opacity={0.9} />
      <text x={W - R + 9} y={y(z) + 3.5} fontSize={11} fontWeight={600} fill={col}>{lab}</text>
    </g>
  );

  const ticks: number[] = [];
  for (let z = -Math.floor(bound); z <= Math.floor(bound); z++) ticks.push(z);
  const dates = series[0]!.pts.map(p => p.d);
  const every = Math.max(1, Math.ceil(n / 7));

  return (
    <div className={`cc-plotwrap${t.dark ? ' cc-dark' : ''}`}>
      <div className="cc-chartbox">
        <svg viewBox={`0 0 ${W} ${H}`} className="cc-chart" role="img"
          aria-label={`Overlaid control chart, ${series.length} parameters standardised to z-scores`}>
          <rect width={W} height={H} fill={t.bg} />
          {ticks.map(z => (
            <g key={z}>
              <line x1={L} y1={y(z)} x2={W - R} y2={y(z)} stroke={t.grid} />
              <text x={L - 11} y={y(z) + 4} textAnchor="end" fontSize={11.5} fill={t.axis}>
                {z > 0 ? '+' : ''}{z}
              </text>
            </g>
          ))}
          {ln(3, t.red, 'UCL +3SD', '7 4')}{ln(2, t.grn, 'UWL +2SD', '4 3')}
          {ln(0, t.ink, 'Mean')}
          {ln(-2, t.grn, 'LWL −2SD', '4 3')}{ln(-3, t.red, 'LCL −3SD', '7 4')}

          {series.map(s => (
            <g key={s.name}>
              <path d={s.pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.z).toFixed(1)}`).join(' ')}
                fill="none" stroke={s.col} strokeWidth={1.6} strokeLinejoin="round"
                opacity={0.92} strokeDasharray={s.dash || undefined} />
              {s.pts.map((p, i) => {
                const bad = Math.abs(p.z) > 3;
                const wrn = !bad && Math.abs(p.z) > 2;
                const d = {
                  title: s.name, colour: s.col, date: fmtLocalDate(p.d), label: lbl,
                  value: String(p.v), z: `${p.z > 0 ? '+' : ''}${p.z.toFixed(2)}`,
                  status: bad ? 'Beyond a control limit' : wrn ? 'Beyond a warning limit' : 'In control',
                  kind: (bad ? 'bad' : wrn ? 'warn' : 'ok') as 'ok' | 'warn' | 'bad',
                };
                return (
                  <circle key={i} cx={x(i)} cy={y(p.z)} r={bad ? 5 : 3.2}
                    fill={s.col} stroke={t.bg} strokeWidth={1.2} className="cc-pt"
                    onMouseEnter={e => place(e, d)} onMouseMove={e => place(e, d)} onMouseLeave={hide} />
                );
              })}
            </g>
          ))}

          {dates.map((d, i) => i % every ? null : (
            <text key={`d${i}`} x={x(i)} y={T + ih + 22} textAnchor="middle"
              fontSize={11.5} fill={t.axis}>{fmtLocalDate(d)}</text>
          ))}
        </svg>
      </div>
      <Tip t={tip} />
    </div>
  );
}
